import {Avatar, Icon} from '@chakra-ui/react'
import {useState, useEffect} from 'react'
import {RiUserLine, RiBankLine} from 'react-icons/ri'

interface PremiumAvatarProps {
  src?: string
  name?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  variant?: 'dancer' | 'school'
  borderRadius?: string
}

const sizeMap: Record<'sm' | 'md' | 'lg' | 'xl' | '2xl', {boxSize: string; iconSize: number}> = {
  sm: {boxSize: '32px', iconSize: 16},
  md: {boxSize: '48px', iconSize: 24},
  lg: {boxSize: '64px', iconSize: 32},
  xl: {boxSize: '128px', iconSize: 64},
  '2xl': {boxSize: '290px', iconSize: 120},
}

export const PremiumAvatar = ({
  src,
  name,
  size = 'md',
  variant = 'dancer',
  borderRadius = 'full',
}: PremiumAvatarProps) => {
  const [imageLoaded, setImageLoaded] = useState(false)
  const dimensions = sizeMap[size]
  const FallbackIcon = variant === 'school' ? RiBankLine : RiUserLine

  useEffect(() => {
    setImageLoaded(false)

    if (!src) {
      return
    }

    let isMounted = true
    const img = new Image()

    img.onload = () => {
      if (isMounted) setImageLoaded(true)
    }
    img.onerror = () => {
      if (isMounted) setImageLoaded(false)
    }
    img.src = src

    return () => {
      isMounted = false
      img.onload = null
      img.onerror = null
      img.src = ''
    }
  }, [src])

  const hasImage = src && imageLoaded

  return (
    <Avatar
      src={hasImage ? src : undefined}
      name={name}
      icon={<Icon as={FallbackIcon} boxSize={`${dimensions.iconSize}px`} color="gray.400" />}
      boxSize={dimensions.boxSize}
      bg={hasImage ? 'white' : 'brand.surface02'}
      outline="1px solid"
      outlineColor='brand.accent'
      borderRadius={borderRadius}
      boxShadow='0 2px 8px rgba(201, 174, 111, 0.15)'
      loading='lazy'
      sx={{
        '& img': {
          objectFit: variant === 'school' ? 'contain' : 'cover',
          padding: variant === 'school' ? '6%' : 0,
        },
      }}
    />
  )
}
